/*
 *  -----------------------------------------------------------------------
 *    <copyright file="charts-wrapper.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useCallback, useEffect, useState } from 'react';
import PanelConstants from '../panels/add-panel/panel-constants';
import NavigationService from '@components-clean/page-nav/navigation.service';
import { useBoolean, useWindowSize } from 'hooks';
import {
    PanelFields,
    DataSetFields,
    CentralMonitoringActionFields,
    CentralMonitoringObservationAndActionFields,
} from 'queries/generated/fragments.generated';
import { PanelType } from 'types/graphql-global-types';
import Constants from 'utils/constants';
import { formatDateUTC_DEPRECATED } from 'utils/convertor';
import ActionsDonutChart from 'views/components/central-monitoring/site-dashboard-summary/actions-donut-chart';
import KriScoresChart from 'views/components/central-monitoring/site-dashboard-summary/kri-scores-chart';
import ObservationsDonutChart from 'views/components/central-monitoring/site-dashboard-summary/observations-donut-chart';
import PanelTrendChart from 'views/components/central-monitoring/site-dashboard-summary/panel-trend-chart';
import CustomPager from 'views/components/core/custom-pager';

const ChartsWrapper = ({
    dataSets,
    datasetDate,
    scoringData,
    actions,
    observations,
    panels,
    siteInfo,
    siteConfig,
}: {
    dataSets: DataSetFields[];
    datasetDate: any;
    scoringData: any;
    actions: CentralMonitoringActionFields[];
    observations: CentralMonitoringObservationAndActionFields[];
    panels: PanelFields[];
    siteInfo: any;
    siteConfig: any;
}) => {
    const size = useWindowSize();

    const isCollapsed = useBoolean(false);
    const isSidebarOpen = useBoolean(true);
    const disabled = useBoolean(false);
    const [itemsForPage, setItemsForPage] = useState(size.width > 1200 ? 4 : 2);
    const [itemsForCharts, setItemsForCharts] = useState([]);
    const [containerWidth, setContainerWidth] = useState(0);

    useEffect(() => {
        const subscribe = NavigationService.onUpdateSideBar().subscribe((isOpen: boolean) => {
            isSidebarOpen.setValue(isOpen);
        });
        return () => {
            subscribe.unsubscribe();
        };
    }, []);

    const panelContainerRef = useCallback(
        (node) => {
            setTimeout(() => {
                if (node !== null) setContainerWidth(node.clientWidth);
            }, 0);
        },
        [size, isSidebarOpen.value],
    );

    useEffect(() => {
        let amountPerPage = size.width >= 1070 ? Math.floor(containerWidth / 500) * 2 : 2;
        disabled.setValue(itemsForCharts?.length <= amountPerPage / 2);
        if (isCollapsed.value) amountPerPage /= 2;
        setItemsForPage(amountPerPage);
        if (itemsForPage !== amountPerPage) NavigationService.legacySetReflowChart();
    }, [size, isCollapsed.value, containerWidth, itemsForCharts]);

    useEffect(() => {
        if (siteConfig) {
            const items = [
                {
                    id: 1,
                    type: 'KRI_SCORES',
                    panel: null as any,
                    data: null as any,
                },
            ];

            let newId = 2;
            const scores = scoringData;

            // Add all non-trend panel models
            (panels || []).forEach((panel: any) => {
                if (
                    panel.panelType === PanelType.MODEL &&
                    (panel.modelParams.type === PanelConstants.modelOptions.FUNNEL ||
                        panel.modelParams.type === PanelConstants.modelOptions.RATE_GROUP ||
                        panel.modelParams.type === PanelConstants.modelOptions.RATEGROUP ||
                        panel.modelParams.type === PanelConstants.modelOptions.TRI_OPERATIONAL ||
                        panel.modelParams.type === PanelConstants.modelOptions.TRIOPERATIONAL)
                ) {
                    if (!panel || !scores?.siteScores?.length || !siteInfo?.Fields) {
                        console.log('No site scores or fields found for panel', panel, scores, siteInfo);
                        return;
                    }

                    // Get the correct site scores
                    const siteScores = scores.siteScores.find((s: any) => s.siteId === siteInfo.Fields['Site.id']);
                    if (!siteScores || !siteScores.indicatorScores?.length) {
                        console.log('No site scores or indicator scores found for panel', panel, scores, siteScores);
                        return;
                    }

                    // Get the correct panel scores
                    const indicator = siteScores.indicatorScores.find((s: any) => s.panelId === panel.id);
                    if (!indicator?.previousScores?.length) {
                        console.log('No previous indicator scores found for panel', panel, siteScores, indicator);
                        return;
                    }

                    const getColor = (c: number) => {
                        switch (c) {
                            case 0:
                                return Constants.Colors.EMPTY;
                            case 1:
                                return Constants.Colors.RED;
                            case 2:
                                return Constants.Colors.AMBER;
                            case 3:
                                return Constants.Colors.GREEN;
                            case 4:
                                return Constants.Colors.PURPLE;
                            default:
                                return Constants.Colors.EMPTY;
                        }
                    };

                    const data = indicator.previousScores
                        .map((s: any, i: number) => ({
                            color: getColor(s.color),
                            x: new Date(s.dataCutDate),
                            y: Math.round(+s.score ?? 0),
                            date: formatDateUTC_DEPRECATED(s.dataCutDate, true, 'DD-MMM-YYYY'),
                            active: dataSets?.find((ds: any) => ds.id === s.dataSetId)?.active ?? false,
                        }))
                        .filter((d: any) => d.active)
                        .slice(-11);

                    if (!data.length) {
                        console.log('No data found');
                        return;
                    }

                    data.push({
                        color: getColor(indicator.color),
                        x: new Date(scores.dataCutDate),
                        y: +indicator.score ?? 0,
                        date: formatDateUTC_DEPRECATED(scores.dataCutDate, true, 'DD-MMM-YYYY'),
                    });

                    items.push({
                        id: newId++,
                        type: 'PANEL_TREND',
                        panel,
                        data,
                    });
                }
            });

            if (siteConfig.actionChart)
                items.push({
                    id: items.length,
                    type: 'ACTIONS',
                    panel: null,
                    data: null,
                });
            if (siteConfig.observationChart)
                items.push({
                    id: items.length,
                    type: 'OBSERVATIONS',
                    panel: null,
                    data: null,
                });
            setItemsForCharts(items);
        }
    }, [siteConfig, scoringData, siteInfo]);

    return (
        <div ref={panelContainerRef}>
            <CustomPager
                itemsForPage={itemsForPage}
                isCollapseButton
                items={itemsForCharts}
                disabled={disabled.value}
                renderChild={(item: any) => {
                    switch (item.type) {
                        case 'ACTIONS':
                            return !datasetDate ? null : (
                                <ActionsDonutChart actions={actions} datasetDate={datasetDate._date} />
                            );
                        case 'OBSERVATIONS':
                            return !datasetDate ? null : (
                                <ObservationsDonutChart observations={observations} datasetDate={datasetDate._date} />
                            );
                        case 'KRI_SCORES':
                            return <KriScoresChart scoringData={scoringData} panels={panels} siteInfo={siteInfo} />;
                        case 'PANEL_TREND':
                            return (
                                <PanelTrendChart
                                    title={item.panel?.graph.plotTitle}
                                    data={item.data}
                                    customKey={item.panel.id}
                                />
                            );

                        default:
                            return null;
                    }
                }}
                changeItemsForPage={isCollapsed.setValue}
            />
        </div>
    );
};

export default ChartsWrapper;
